import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { LocationStatus, SiteLocationType } from "~/gql/graphqlBase.generated";

interface SiteLocationFormInput {
	address?: {
		addressLine1: string;
		addressLine2: string;
		city: string;
		country: string;
		id: string;
		stateProvince: string;
		toName: string;
		zipcode: string;
	};
	erpId: string;
	name: string;
	siteLocationId: string;
	status: LocationStatus;
	timeZone?: string;
}

interface SubLocationFormInput {
	erpId: string;
	locationId: string;
	locationType: SiteLocationType;
	name: string;
	parentLocationId: string;
	siteLocationId: string;
	status: LocationStatus;
}

type FormInput = SiteLocationFormInput | SubLocationFormInput;

interface SiteLocationState {
	editingNodeType: "siteLocation" | "subLocation" | null;
	formInput?: FormInput;
	selectedSiteLocationId: string | null;
	siteLocationFormOpen: boolean;
}

const initialState: SiteLocationState = {
	editingNodeType: null,
	selectedSiteLocationId: null,
	siteLocationFormOpen: false
};

const siteLocationSlice = createSlice({
	initialState,
	name: "siteLocation",
	reducers: {
		clearSiteLocationFormInput: (state) => {
			state.formInput = undefined;
		},
		closeSiteLocationForm: (state) => {
			state.siteLocationFormOpen = false;
		},
		openSiteLocationForm: (state) => {
			state.siteLocationFormOpen = true;
		},
		setEditingNodeType: (state, action: PayloadAction<"siteLocation" | "subLocation" | null>) => {
			state.editingNodeType = action.payload;
		},
		setFormInput: (state, action: PayloadAction<FormInput | null>) => {
			if (action.payload === null) {
				state.formInput = undefined;
			} else {
				state.formInput = action.payload;
			}
		},
		setSelectedSiteLocation: (state, action: PayloadAction<string | null>) => {
			state.selectedSiteLocationId = action.payload;
		},
		updateFormInput: (state, action: PayloadAction<Partial<FormInput>>) => {
			if (state.formInput) {
				state.formInput = { ...state.formInput, ...action.payload };
			}
		}
	}
});

export const {
	clearSiteLocationFormInput,
	closeSiteLocationForm,
	openSiteLocationForm,
	setEditingNodeType,
	setFormInput,
	setSelectedSiteLocation,
	updateFormInput
} = siteLocationSlice.actions;

export type { FormInput, SiteLocationFormInput, SiteLocationState, SubLocationFormInput };

export default siteLocationSlice.reducer;
